import React from "react";
import { motion } from "framer-motion";
import { Activity, ShieldCheck, Headphones, Globe2, Layers, Plug, Lock } from "lucide-react";

const FEATURES = [
  { Icon: Activity, title: "Smart Monitoring", desc: "Live telemetry across your stack — latency, packet loss, uptime." },
  { Icon: ShieldCheck, title: "Complete Safety", desc: "From CCTV to biometric access, every layer is hardened." },
  { Icon: Headphones, title: "Expert Support", desc: "24/7 support with field-engineers on call." },
  { Icon: Globe2, title: "Reliable Network", desc: "Certified cabling, redundant designs, measurable SLAs." },
  { Icon: Layers, title: "One Platform · Many Solutions", desc: "Single partner for networking, security and electrical." },
  { Icon: Plug, title: "Seamless Integration", desc: "Hardware, software and existing infra — connected." },
  { Icon: Lock, title: "Advanced Security", desc: "Layered protection from physical to network perimeter." },
];

const WhyUs = () => {
  return (
    <section id="why-us" data-testid="why-us-section" className="relative py-24 md:py-32 overflow-hidden bg-[#080F1B]">
      <div className="absolute inset-0 bg-grid-faint" />
      <div className="orb bg-[#FF6B00]/10 w-[420px] h-[420px] top-0 -left-32" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-14">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF6B00] mb-4">
            ◆ Why HRN Networks
          </div>
          <h2 data-testid="why-us-title" className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tighter">
            Engineered for excellence. <br />
            <span className="text-[#A0B2C6]">Trusted by businesses that can't go offline.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="why-us-grid">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className={`group relative beam-border rounded-2xl bg-[#0B1320] border border-white/8 p-6 hover:bg-[#111C2E] transition-all ${
                i === 0 ? "lg:col-span-2 lg:row-span-2" : ""
              }`}
              data-testid={`why-us-item-${f.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#FF6B00]/12 border border-[#FF6B00]/25 flex items-center justify-center text-[#FF6B00] mb-4">
                <f.Icon size={20} strokeWidth={1.6} />
              </div>
              <h4 className="font-display text-lg font-semibold tracking-tight">{f.title}</h4>
              <p className="mt-2 text-sm text-[#A0B2C6] leading-relaxed">{f.desc}</p>

              {i === 0 && (
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {["Latency", "Uptime", "Packet"].map((l, idx) => (
                    <div key={l} className="border border-white/8 p-3 bg-[#060B14]/40">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A0B2C6]">{l}</div>
                      <div className="font-display text-2xl font-bold mt-1 text-white">
                        {idx === 0 ? "12ms" : idx === 1 ? "99.9%" : "0.1%"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
